/**
 * The alert history (phase 7b item 4): every raise, resolve and event
 * occurrence, newest first, with the channels each went to — a filter by
 * type, and "Load more" for the next page.
 */
import type { AlertHistoryEntryWire } from '@acp/api-contract/alerts.js';
import { ALERT_TYPES, type AlertType } from '@acp/tenant-alerts/catalog.js';
import { alertLabel, severityTone } from '../../lib/alerts.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { NativeSelect } from '../ui/native-select.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type AlertHistoryProps = {
  readonly entries: readonly AlertHistoryEntryWire[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly type: AlertType | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly onType: (type: AlertType | null) => void;
  readonly onLoadMore: () => void;
};

const TRANSITION: Readonly<Record<AlertHistoryEntryWire['transition'], string>> = { raised: 'Raised', resolved: 'Resolved', occurred: 'Occurred', acknowledged: 'Acknowledged' };

export function AlertHistory({ entries, loading, error, type, hasMore, loadingMore, onType, onLoadMore }: AlertHistoryProps) {
  return (
    <Card data-testid="alert-history">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>History</CardTitle>
          <CardDescription>The last 90 days: what was raised, resolved or happened, and who was told.</CardDescription>
        </div>
        <NativeSelect
          aria-label="Alert type"
          className="w-64"
          value={type ?? ''}
          onChange={(event) => {
            const picked = ALERT_TYPES.find((t) => t === event.target.value);
            onType(picked ?? null);
          }}
          data-testid="history-type"
        >
          <option value="">Every type</option>
          {ALERT_TYPES.map((t) => (
            <option key={t} value={t}>
              {alertLabel(t)}
            </option>
          ))}
        </NativeSelect>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error !== null ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load the history: {error}
          </p>
        ) : null}
        {loading && entries.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p> : null}
        {!loading && error === null && entries.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="history-empty">
            Nothing yet.
          </p>
        ) : null}
        {entries.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-44">When</TableHead>
                <TableHead className="w-24">What</TableHead>
                <TableHead>Alert</TableHead>
                <TableHead className="w-48">Sent to</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.entryId} data-testid="history-row">
                  <TableCell className="text-sm tabular-nums">{e.at.replace('T', ' ').slice(0, 19)}</TableCell>
                  <TableCell>
                    <Badge variant={e.transition === 'resolved' || e.transition === 'acknowledged' ? 'success' : severityTone(e.severity)}>{TRANSITION[e.transition]}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{alertLabel(e.type)}</div>
                    <div className="text-sm text-muted-foreground">
                      {e.summary}
                      {e.by === null ? '' : ` — acknowledged by ${e.by}`}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{e.channelIds.length === 0 ? '—' : e.channelIds.join(', ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        {hasMore ? (
          <Button variant="outline" className="justify-self-center" onClick={onLoadMore} disabled={loadingMore} data-testid="history-more">
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
